import React, { useState, useRef } from "react"
import { makeStyles } from "@material-ui/styles"
import { TextField, Grid, IconButton, Box, Divider } from "@material-ui/core"
import AddPhotoAlternateOutlinedIcon from "@material-ui/icons/AddPhotoAlternateOutlined"
import AlternateEmailOutlinedIcon from "@material-ui/icons/AlternateEmailOutlined"
import Config from "react-global-configuration"
import userflow from "userflow.js"
import moment from "moment"
import { useMutationCreateAction, useMutationUpdateAction } from "../../data"
import {
  MentionTextInput,
  UsersGroupsAssigner,
  DraftBlockquote,
  CreatorActions,
  InfoSection,
  PaperBox,
  FormatDate,
  usersGroupsEveryoneItem,
} from ".."
import { toId, mapToIds, useMountEffect, useFormUtils, useDraft } from "../../utils"
import { CreatorMaster } from "../Creators"
import { Uploader, UploadingList, UploadingImagesList } from "../Uploader"
import { useAuth } from "../../services"

const useStyles = makeStyles((theme) => ({
  settings: {
    minHeight: 68,
  },
  scheduledText: {
    color: theme.palette.scheduled.main,
  },
  dividerSpacer: {
    marginBottom: theme.spacing(2),
  },
  actions: {
    paddingTop: theme.spacing(1),
    fontSize: 14,
  },
  buttonText: {
    fontWeight: "600",
    fontSize: 14,
  },
  postOptions: {
    padding: theme.spacing(3),
  },
  visibleFrom: {},
}))

const initialState = {
  title: "",
  content: null,
  users: [],
  groups: [],
  uploads: [],
}

const ActionCreator = ({ open, job, edit, step, onClose, isInline }) => {
  const classes = useStyles()
  const [createAction, { loading: createLoading }] = useMutationCreateAction()
  const [updateAction, { loading: updateLoading }] = useMutationUpdateAction()
  const { isValid, touched } = useFormUtils()
  const {
    support: {
      userflow: { enabled: userflowEnabled },
    },
  } = Config.get()
  const {
    settings: { locations: userLocations },
  } = useAuth()

  const [title, setTitle] = useState(initialState.title)
  const [content, setContent] = useState(initialState.content)
  const [hasEveryone, setHasEveryone] = useState(false)
  const [users, setUsers] = useState(initialState.users)
  const [groups, setGroups] = useState(initialState.groups)
  const [uploads, setUploads] = useState(initialState.uploads)
  const [uploading, setUploading] = useState([])
  const [isUploading, setIsUploading] = useState(false)
  const contentRef = useRef(null)

  const [initialPicked, setInitialPicked] = useState(null)

  const [draft, setDraft, removeDraft, draftUntouched] = useDraft(
    `action_creator_${job ? toId(job) : "new"}_${step ? `_${toId(step)}` : ""}`,
    null
  )

  useMountEffect(() => {
    if (edit) {
      const noUsersOrGroups = !edit.users.length && !edit.groups.length

      setTitle(edit.title)
      setContent(edit.content)
      setUsers(mapToIds(edit.users))
      setGroups(mapToIds(edit.groups))
      setUploads(edit.uploads)
      setHasEveryone(!edit.users.length && !edit.groups.length)

      setInitialPicked(noUsersOrGroups ? [usersGroupsEveryoneItem] : [...edit.users, ...edit.groups])
    } else {
      setInitialPicked([])

      if (draft) {
        draft.title && setTitle(draft.title)
        draft.content && setContent(draft.content)
        draft.uploads && setUploads(draft.uploads)
      }
    }
  }, [])

  const handleClose = (event) => {
    onClose && onClose(event)
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (formValid()) {
      const usersGroups = { users: hasEveryone ? [] : users, groups: hasEveryone ? [] : groups }
      const uploadIds = uploads.map((upload) => upload.id)
      const variables = {
        title,
        content,
        job: toId(job),
        jobStep: toId(step),
        ...usersGroups,
        uploads: uploadIds,
      }
      if (edit) {
        await updateAction({
          variables: {
            id: toId(edit),
            ...variables,
          },
        })
        if (userflowEnabled) userflow.track("updated_action")
      } else {
        await createAction({
          variables,
        })
        removeDraft()
        if (userflowEnabled) userflow.track("created_action")
      }
      handleClose()
    }
  }

  const handleChange = (set, name, newValue) => {
    set(newValue)
    if (!edit) {
      setDraftProperty(name, newValue)
    }
  }

  const setDraftProperty = (prop, value) => {
    setDraft((prev) => ({
      ...prev,
      [prop]: value,
    }))
  }

  const handleDiscardDraft = () => {
    removeDraft()
    setTitle(initialState.title)
    setContent(initialState.content)
    setUploads(initialState.uploads)
  }

  const handleUsersGroupsPickedChanged = (picked) => {
    const newHasEveryone = picked.find((p) => p.__typename === "Everyone")
    setHasEveryone(newHasEveryone)
    setUsers(newHasEveryone ? [] : [...mapToIds(picked.filter((p) => p.__typename === "User"))])
    setGroups(newHasEveryone ? [] : [...mapToIds(picked.filter((p) => p.__typename === "Group"))])
  }

  const handleAddMention = (id) => {
    const parts = id.split(":")
    if (parts.length >= 2) {
      const newId = parts[1]
      switch (parts[0]) {
        case "User":
          if (!users.includes(newId)) setUsers([...users, newId])
          break
        case "Group":
          if (!groups.includes(newId)) setGroups([...groups, newId])
          break
        default:
      }
    }
  }

  const handleAtClick = () => {
    setContent(content ? `${content.trimEnd()} @` : "@")
    contentRef.current.focus()
  }

  const handleBeforeUpload = (items) => {
    setIsUploading(true)
    setUploading(() => items)
  }
  const handleUploadProgress = (items) => {
    setUploading(() => items)
  }
  const handleItemUploaded = (item) => {
    setUploads((state) => {
      const result = [...state, item]
      if (!edit) {
        setDraftProperty("uploads", [...result])
      }
      return result
    })
  }
  const handleRemoveUpload = (id) => {
    setUploads((state) => {
      const result = [...state.filter((upload) => upload.id !== id)]
      if (!edit) {
        setDraftProperty("uploads", [...result])
      }
      return result
    })
  }
  const handleNotUploading = () => {
    setIsUploading(false)
  }

  const formValid = () => isValid(title, content) && (hasEveryone || users.length || groups.length) && !isUploading

  const isError = (val) => (val != null ? val.length === 0 : false)
  const errorMessage = (val, message) => isError(val) && message

  const isFormValid = formValid()

  const showDraft = !!draft && draftUntouched

  const locations = job?.location ? [job.location] : [...userLocations]
  const location = job?.location ? userLocations.find((l) => toId(l) === toId(job.location)) : null

  const loading = createLoading || updateLoading

  const form = (
    <>
      <DraftBlockquote show={showDraft} subject="Action" onDiscard={handleDiscardDraft} />

      {Boolean(job) && Boolean(step) && (
        <PaperBox fullWidth mb={2}>
          <InfoSection title="Location" value={location.name} />
          <Divider />
          <InfoSection
            title="Job"
            value={
              <>
                {job.title} - <FormatDate value={moment()} />
              </>
            }
          />
          <Divider />
          <InfoSection title="Step" value={step.name} />
        </PaperBox>
      )}

      <Box mb={1}>
        <TextField
          variant="outlined"
          margin="normal"
          fullWidth
          id="title"
          label="Title"
          name="title"
          value={title}
          required
          onChange={(event) => handleChange(setTitle, "title", event.target.value)}
          inputProps={{ "data-cy": "ActionCreator-title" }}
          autoFocus
        />
      </Box>
      <MentionTextInput
        inputRef={contentRef}
        label="Describe what needs to be done"
        value={content}
        locations={locations}
        height={190}
        onChange={(event) => handleChange(setContent, "content", event.target.value)}
        onAddMention={handleAddMention}
        fullWidth
        multiline
        required
        onBlur={() => touched(content, setContent)}
        error={isError(content)}
        helperText={errorMessage(content, "Please enter content")}
        cy="ActionCreator-content"
      />
      {!!uploads?.length && (
        <Box my={1}>
          <UploadingImagesList
            uploaded={uploads}
            uploading={uploading}
            onRemoveUpload={handleRemoveUpload}
            height={100}
            gridItemProps={{
              xs: 3,
            }}
          />
          <UploadingList uploaded={uploads} uploading={uploading} images={false} onRemoveUpload={handleRemoveUpload} />
        </Box>
      )}
      <Grid container direction="row" justifyContent="space-between" alignItems="center" className={classes.settings}>
        <Grid item sm={3} xs={12}>
          <IconButton onClick={handleAtClick} title="Link this jobs, articles or people to this post">
            <AlternateEmailOutlinedIcon />
          </IconButton>
          <Uploader
            onBeforeUpload={handleBeforeUpload}
            onProgress={handleUploadProgress}
            onItemUploaded={handleItemUploaded}
            onAfterUpload={handleNotUploading}
            onError={handleNotUploading}
            images={false}
            disabled={isUploading}
            title="Attach files to this post"
          />
          <Uploader
            onBeforeUpload={handleBeforeUpload}
            onProgress={handleUploadProgress}
            onItemUploaded={handleItemUploaded}
            onAfterUpload={handleNotUploading}
            onError={handleNotUploading}
            icon={<AddPhotoAlternateOutlinedIcon />}
            documents={false}
            disabled={isUploading}
            title="Attach photos / images to this post"
          />
        </Grid>
      </Grid>

      {initialPicked && (
        <Box mb={2}>
          <UsersGroupsAssigner
            title="Assign to"
            placeholder="Nobody selected"
            allowAll={false}
            allowSelf={false}
            allowGroups
            allStaffLabel="All staff"
            locations={mapToIds(locations)}
            initialPicked={initialPicked}
            onPickedChanged={handleUsersGroupsPickedChanged}
            cy="ActionCreator-to"
          />
        </Box>
      )}

      <CreatorActions
        id="ActionCreator-CreatorActions"
        submitLabel={edit ? "Update action" : "Raise action"}
        submitLoading={loading}
        onClose={handleClose}
        onSubmit={handleSubmit}
        disableSubmit={!isFormValid}
      />
    </>
  )

  return (
    <CreatorMaster
      id="ActionCreator-dialog"
      open={open}
      title={edit ? "Update action" : "Raise action"}
      form={form}
      isInline={isInline}
      onClose={handleClose}
    />
  )
}

export { ActionCreator }
